import { roundAllCorners } from "../model/constructionRounding";
import paper from "paper";
import { ToolBase } from "./ToolBase";
import { computeDefaultSmoothAnchors } from "../model/constructionAnchors";
import type { PointerSample } from "../canvas/PointerInput";
import { newId } from "../model/ids";
import { withCutout } from "../model/projectOps";
import { pathFromBuffer, type Scene } from "../canvas/Scene";
import { smoothPathFromRawPoints } from "../model/freehandSmoothing";
import { DEFAULT_LEATHER_SMOOTHNESS } from "../model/leatherSmoothing";
import { cssVar } from "../colors";
import { DrawGestureEngine, type DrawGestureHost } from "./drawGestureEngine";
import { nextCutoutName } from "../model/Project";
import { translateCutoutInProject } from "./SelectTool";
import { resolveDrawTarget } from "./drawTarget";
import { translatePathData } from "../model/pieceTransform";
import {
  angleModifierHeld,
  constrainSquare,
  isAngleSnapActive,
  isCenterSnapActive,
  isSizesSnapActive,
  resolveDragBox,
  resolveDragAxis,
  snapToCommonAngle,
  type Point2,
} from "./dragSnap";
import {
  gatherDrawSnapTargets,
  resolveDrawSnap,
  type DrawSnapResult,
  type DrawSnapTargets,
} from "./drawSnap";
import { setAngleHint } from "../snapAngleIndicator";
import {
  RegularPolygonGesture,
  type PolygonPreviewShape,
  type Vec2,
} from "./regularPolygonGesture";
import {
  CLOSE_SNAP_RADIUS,
  closeSnapRadius,
  emitSegmentRay,
} from "./drawGesture";
import { DimensionLabel } from "../canvas/DimensionLabel";
import {
  offsetSourceOutlines,
  OutlineOffsetController,
  type OffsetCandidate,
  type OutlineOffsetHost,
} from "./outlineOffsetController";
import { showCanvasToast } from "../canvasToast";

export type CutoutKind =
  | "offset"
  | "rect"
  | "ellipse"
  | "freehand"
  | "polygon"
  | "slot";

const PREVIEW_STROKE = () => cssVar("--piece-cut");
// Exported so the leather Shape tool's "pill" kind can mirror the same
// defaults without a second copy of the constants drifting out of sync.
export const SLOT_DEFAULT_WIDTH_MM = 6;
export const SLOT_DEFAULT_LENGTH_RATIO = 4; // click-without-drag length = 4× width

/**
 * Build a "stadium" (rounded-rectangle / capsule) path between two outer end
 * points with a given short-axis width. Returns null if the endpoints are
 * closer than the width — a valid capsule needs the short axis to fit.
 */
export function makeSlotPathData(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  width: number,
): string | null {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const length = Math.hypot(dx, dy);
  if (length < width + 0.001) return null;
  const ux = dx / length;
  const uy = dy / length;
  const r = width / 2;
  // Perpendicular unit vector (rotated 90° CCW).
  const px = -uy;
  const py = ux;
  const innerStart = { x: p0.x + ux * r, y: p0.y + uy * r };
  const innerEnd = { x: p1.x - ux * r, y: p1.y - uy * r };
  const tl = { x: innerStart.x + px * r, y: innerStart.y + py * r };
  const bl = { x: innerStart.x - px * r, y: innerStart.y - py * r };
  const tr = { x: innerEnd.x + px * r, y: innerEnd.y + py * r };
  const br = { x: innerEnd.x - px * r, y: innerEnd.y - py * r };
  // Two 180° arcs cap the rectangle. SVG: A rx ry x-axis-rot large-arc sweep x y.
  // sweep=0 (CCW in y-down) makes both caps bulge outward; sweep=1 would
  // bulge them inward and produce a "barbell" with bitten ends.
  return (
    `M ${tl.x} ${tl.y} L ${tr.x} ${tr.y} ` +
    `A ${r} ${r} 0 0 0 ${br.x} ${br.y} ` +
    `L ${bl.x} ${bl.y} ` +
    `A ${r} ${r} 0 0 0 ${tl.x} ${tl.y} Z`
  );
}

/**
 * Cuts a shape out of a leather part. Mirrors ShapeTool's primitives:
 *   rect / ellipse — drag from start to current corner.
 *   freehand       — drag a closed path.
 *   polygon        — click to drop points; click near the first point or
 *                    double-click to close.
 *
 * Live ghost: a dashed red preview of the shape follows the cursor while
 * drawing. Polygon previews include the in-progress segment from last point
 * to cursor on hover.
 *
 * Rejects placement if the resulting cutout's centroid + bounds don't
 * overlap any part — same rule as StampTool / HoleTool.
 */
export class CutoutTool
  extends ToolBase
  implements DrawGestureHost, OutlineOffsetHost
{
  id = "cutout";
  label = "Cutout";
  shortcut = "x";
  cursor = "crosshair";
  kind: CutoutKind = "rect";
  /** The Offset shape kind's gesture (#972), shared with ShapeTool. */
  private offset = new OutlineOffsetController(this);
  /** Width (short axis) of the slot capsule in project mm. Set via the
   *  cutout tool's preset chips in the Properties panel. */
  slotWidth = SLOT_DEFAULT_WIDTH_MM;

  /** Shared click-to-add polygon gesture (#804 IE3). This tool OWNS the engine
   *  and implements `DrawGestureHost`; only the polygon kind drives it. The
   *  drag-out kinds + the drag-existing-cutout move stay tool-owned. */
  private drawEngine = new DrawGestureEngine(this);

  /** Shared regular-polygon gesture (#928/#931/#934) — the same state machine
   *  the construction Draw tool runs (auto-n two-stage flow, click-move-click,
   *  pinned-size aim stage, pinned-circle variable count). Ghosts render in
   *  the cutout preview style; commits go through `commitCutout` (target
   *  resolve + overlap guard #779). */
  private polyGesture = new RegularPolygonGesture({
    getScope: () => this.ctx.scene.getPaperScope(),
    getScene: () => this.ctx.scene,
    getViewportView: () => this.ctx.viewport.view,
    getSettings: () => this.ctx.settings,
    renderPreview: (shape) => this.renderPolygonGhost(shape),
    clearPreview: () => this.cleanupPreview(),
    commitVertices: (pts) => this.commitPolygonVertices(pts),
    notifyDraftChange: () => this.drawEngine.notifyDraftChange(),
    liveSides: (sides, radiusMm) => this.polygonLiveCb?.(sides, radiusMm),
    liveSideLength: (mm) => this.polygonLiveSideLenCb?.(mm),
    liveRadius: (mm) => this.polygonLiveRadiusCb?.(mm),
    liveClear: () => this.polygonLiveClearCb?.(),
  });
  /** Live panel mirror for the polygon gesture (#931/#934) — injected by
   *  app.ts, same contract as the construction tool's. */
  private polygonLiveCb: ((sides: number, radiusMm: number) => void) | null =
    null;
  private polygonLiveSideLenCb: ((mm: number) => void) | null = null;
  private polygonLiveRadiusCb: ((mm: number) => void) | null = null;
  private polygonLiveClearCb: (() => void) | null = null;

  /** Re-render an in-flight regular-polygon stage after a panel value edit
   *  (#934) — app.ts calls this on settings changes. */
  refreshPolygonGesture(): void {
    this.polyGesture.refresh();
  }

  setPolygonLiveCallbacks(
    live: (sides: number, radiusMm: number) => void,
    liveSideLength: (mm: number) => void,
    liveRadius: (mm: number) => void,
    clear: () => void,
  ): void {
    this.polygonLiveCb = live;
    this.polygonLiveSideLenCb = liveSideLength;
    this.polygonLiveRadiusCb = liveRadius;
    this.polygonLiveClearCb = clear;
  }

  /** Ghost renderer for the shared polygon gesture — the same thin dashed
   *  cut-red stroke every cutout drag preview uses. */
  private renderPolygonGhost(shape: PolygonPreviewShape): void {
    const scope = this.ctx.scene.getPaperScope();
    this.cleanupPreview();
    const item =
      shape.kind === "line"
        ? new scope.Path({
            segments: [
              new scope.Point(shape.a.x, shape.a.y),
              new scope.Point(shape.b.x, shape.b.y),
            ],
          })
        : new scope.Path({
            segments: shape.vertices.map((v) => new scope.Point(v.x, v.y)),
            closed: true,
          });
    item.strokeColor = new scope.Color(PREVIEW_STROKE());
    item.strokeWidth = 0.75;
    item.dashArray = [4, 2];
    item.fillColor = null;
    item.data = { ephemeral: true };
    this.previewItem = item;
  }

  /** Commit a finished regular-polygon ring as a cutout — same sink the
   *  click-to-add `commitDraft` uses (overlap guard #779 included). */
  private commitPolygonVertices(pts: Vec2[]): void {
    const data = pathFromBuffer(pts.map((p) => ({ x: p.x, y: p.y }))) + " Z";
    this.commitCutout(data);
  }

  private startPt: paper.Point | null = null;
  private currentPt: paper.Point | null = null;
  private buffer: { x: number; y: number }[] = [];
  /** Smoothness seeded onto the next freehand cutout (#1016). */
  private freehandSmoothness = DEFAULT_LEATHER_SMOOTHNESS;

  /** Tool-level default for the next freehand trace (#1016), set from the tool
   *  settings panel and persisted across sessions. */
  setFreehandSmoothness(v: number) {
    this.freehandSmoothness = v;
  }
  private previewItem: paper.Item | null = null;
  /** Snap targets (existing vertices / hole centres) gathered lazily at the
   *  start of a draw gesture and reused until the project changes (#761). */
  private snapTargets: DrawSnapTargets | null = null;
  /** Most-recent pointer sample / project-space cursor from
   *  `onMove`, captured for `onModifiersChanged` so a mid-drag
   *  Shift press re-fires the preview with the new modifier
   *  state (rect → square, ellipse → circle). */
  private lastSample: PointerSample | null = null;
  private lastP: { x: number; y: number } | null = null;
  /** Live "W × H" readout shown over the rect / ellipse drag preview.
   *  Same colour as the dashed outline below so the two read as one
   *  overlay. */
  private dimensionLabel = new DimensionLabel(PREVIEW_STROKE());

  // Drag-existing state — when the user mousedowns on an existing cutout,
  // we move it instead of starting a new shape.
  private dragPieceId: string | null = null;
  private dragCutoutId: string | null = null;
  private draggedItem: paper.Item | null = null;
  private dragStart: { x: number; y: number } | null = null;
  private dragLast: { x: number; y: number } | null = null;

  /** User-locked bounding-box dimensions for rect/ellipse. `null` means
   *  the dimension comes from the live cursor delta instead. When both
   *  are non-null, a click places a fixed-size cutout (no drag needed). */
  private presetWidth: number | null = null;
  /** Corner radius (mm) a rectangle cutout is DRAWN with (#1069) — set by a
   *  picked standard size made with rounded corners, or typed in. Separate
   *  from the Round tool's preset. Null draws sharp corners. */
  private drawCornerRadius: number | null = null;
  private presetHeight: number | null = null;
  /** Whether the in-flight rect / ellipse drag grows symmetrically about
   *  its start point ("draw from centre") — latched per sample from
   *  {@link isCenterSnapActive} (Alt held or the centre snap toggle) so
   *  `resolveBoundingBox` can read it without a pointer sample. */
  private drawFromCenter = false;
  /** Fired during onMove so the properties panel can mirror the in-progress
   *  drag in its inputs. Receives the effective bounding-box dimensions in
   *  project mm. */
  private liveDimsCallback?: (w: number, h: number) => void;

  setKind(kind: CutoutKind) {
    this.kind = kind;
    this.offset.cancel();
    this.cancelDraft();
    this.cleanupPreview();
  }

  setSlotWidth(width: number) {
    if (width <= 0) return;
    this.slotWidth = width;
    this.kind = "slot";
  }

  /** Set the corner radius the next rectangle cutout is drawn with (#1069). */
  setDrawCornerRadius(mm: number | null) {
    this.drawCornerRadius = mm;
  }

  setPresetWidth(w: number | null) {
    this.presetWidth = w;
  }

  setPresetHeight(h: number | null) {
    this.presetHeight = h;
  }

  setLiveDimsCallback(fn: ((w: number, h: number) => void) | undefined) {
    this.liveDimsCallback = fn;
  }

  override onActivate() {
    this.snapTargets = null;
    this.cancelDraft();
  }

  /** Snap a raw cursor point onto a nearby vertex / the grid and light the
   *  in-draw snap indicator. Targets are cached until the project changes. */
  private snap(p: Point2): DrawSnapResult {
    if (!this.snapTargets) {
      const scope = this.ctx.scene.getPaperScope();
      this.snapTargets = gatherDrawSnapTargets(
        this.ctx.history.state,
        scope,
        this.ctx.scene.getPartLayoutView(),
      );
    }
    const result = resolveDrawSnap(this.ctx, p, this.snapTargets);
    this.ctx.scene.setDrawSnapPoint(result.hit?.pos ?? null);
    return result;
  }

  override onDown(s: PointerSample, p: { x: number; y: number }) {
    const scope = this.ctx.scene.getPaperScope();
    const pt = new scope.Point(p.x, p.y);

    // Offset kind (#972): a press on a part or cutout edge grabs it and the
    // drag lays a parallel outline inset from it.
    if (this.kind === "offset") {
      this.offset.handleDown(p);
      return;
    }

    // If the user clicked an existing cutout (and we're not in polygon mode,
    // which is click-to-add-points), start dragging it.
    if (this.kind !== "polygon") {
      const hit = this.ctx.scene.hitTest(pt);
      if (hit.cutoutId && hit.pieceId) {
        this.dragPieceId = hit.pieceId;
        this.dragCutoutId = hit.cutoutId;
        this.dragStart = { x: p.x, y: p.y };
        this.dragLast = { x: p.x, y: p.y };
        // setSelection re-renders the scene, so look up the paper item
        // afterwards — otherwise we'd hold a reference to a detached item
        // and translating it during onMove would be a no-op on screen.
        this.ctx.setSelection({
          pieceId: hit.pieceId,
          decorationId: null,
          cutoutId: hit.cutoutId,
        });
        this.draggedItem = this.ctx.scene.getCutoutItem(
          hit.pieceId,
          hit.cutoutId,
        );
        return;
      }
    }

    // Regular-polygon stages (#931/#934): the press commits the aimed /
    // previewed polygon, or ends the click-move-click first side.
    if (this.kind === "polygon" && this.polyGesture.press(s, p)) return;
    if (
      this.kind === "polygon" &&
      !(this.drawEngine.vertexCount === 0 && this.regularPolygonMode(s))
    ) {
      // Click-to-add-vertices gesture, driven by the shared draw engine (#804
      // IE3). Close-snap, the vertex/angle/grid snap tiers, and the commit are
      // injected via the `DrawGestureHost` hooks below. Regular-polygon mode
      // (#928) instead falls through to the drag-out flow below.
      this.drawEngine.onDown(s, p);
      return;
    }
    const start = this.snap(p).point;
    const startPt = new scope.Point(start.x, start.y);
    this.startPt = startPt;
    this.currentPt = startPt;
    this.drawFromCenter = this.shouldSnapCenter(s);
    if (this.kind === "freehand") {
      this.buffer = [{ x: startPt.x, y: startPt.y }];
    }
  }

  override onMove(s: PointerSample, p: { x: number; y: number }) {
    if (this.kind === "offset") {
      this.offset.handleMove(p);
      return;
    }
    this.lastSample = s;
    this.lastP = p;
    if (this.dragLast && this.draggedItem) {
      const dx = p.x - this.dragLast.x;
      const dy = p.y - this.dragLast.y;
      if (dx !== 0 || dy !== 0) {
        const scope = this.ctx.scene.getPaperScope();
        this.draggedItem.translate(new scope.Point(dx, dy));
        this.dragLast = { x: p.x, y: p.y };
      }
      return;
    }
    if (this.kind === "polygon" && !this.startPt) {
      // Polygon vertices are placed on onDown; a press-drag is a no-op for the
      // leather tools (no bezier shaping). Delegate to keep the engine's flow.
      this.drawEngine.onMove(s, p);
      return;
    }
    this.drawFromCenter = this.shouldSnapCenter(s);
    const scope = this.ctx.scene.getPaperScope();
    let pt: { x: number; y: number } = p;
    if (this.kind === "freehand") {
      // Freehand doesn't snap intermediate samples — it would fight the
      // smoothing. Only the start vertex (in onDown) snaps.
      this.ctx.scene.setDrawSnapPoint(null);
    } else {
      const snap = this.snap(p);
      if (snap.hit?.kind === "vertex") {
        // A nearby existing vertex wins over angle / size snap.
        pt = snap.point;
        if (this.kind === "slot") setAngleHint(null);
      } else if (this.startPt) {
        let applied = false;
        if (this.kind === "slot") {
          if (this.shouldSnapAngle(s)) {
            const sn = snapToCommonAngle(this.startPt, p);
            pt = { x: sn.x, y: sn.y };
            setAngleHint(sn.angleRad);
            applied = true;
          } else {
            setAngleHint(null);
          }
        } else if (
          (this.kind === "rect" || this.kind === "ellipse") &&
          this.shouldSnapSizes(s)
        ) {
          pt = constrainSquare(this.startPt, p);
          applied = true;
        }
        if (
          !applied &&
          (snap.hit?.kind === "line" || snap.hit?.kind === "grid")
        )
          pt = snap.point;
      } else if (this.kind === "slot") {
        setAngleHint(null);
      }
    }
    this.currentPt = new scope.Point(pt.x, pt.y);
    // #747: show the slot's drag axis as a length + angle ray while dragging.
    if (this.kind === "slot" && this.startPt) {
      emitSegmentRay(this.ctx.scene, this.startPt, this.currentPt);
    } else {
      this.ctx.scene.setGuideRays(null);
    }
    if (this.kind === "freehand") {
      const last = this.buffer[this.buffer.length - 1];
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1.0) {
        this.buffer.push({ x: p.x, y: p.y });
      }
    }
    // Mirror the in-progress bounding box into the properties panel so the
    // width / height inputs reflect the live drag (and stay frozen on any
    // preset-locked axis). Only rect/ellipse use the dim inputs.
    if (
      (this.kind === "rect" || this.kind === "ellipse") &&
      this.startPt &&
      this.currentPt &&
      this.liveDimsCallback
    ) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      this.liveDimsCallback(box.width, box.height);
    }
    this.updatePreview();
  }

  /** Draw the on-canvas segment ray from `from` to `to` showing its length +
   *  angle (#747 / #729). Clears when degenerate. */
  private shouldSnapAngle(s: PointerSample): boolean {
    return isAngleSnapActive(this.ctx.settings, angleModifierHeld(s));
  }
  private shouldSnapSizes(s: PointerSample): boolean {
    return isSizesSnapActive(this.ctx.settings, s.shiftKey);
  }
  private shouldSnapCenter(s: PointerSample): boolean {
    return isCenterSnapActive(this.ctx.settings, s.altKey);
  }

  override onHover(s: PointerSample, p: { x: number; y: number }) {
    const scope = this.ctx.scene.getPaperScope();
    // Offset kind (#972): the construction / stitch offset cursor treatment —
    // plain pointer plus the perpendicular-arrow affordance over an edge.
    if (this.kind === "offset") {
      this.offset.handleHover(p);
      return;
    }
    // Move-cursor hint when hovering over an existing cutout (only outside
    // polygon mode where click adds a vertex).
    let overCutout = false;
    if (this.kind !== "polygon") {
      const hit = this.ctx.scene.hitTest(new scope.Point(p.x, p.y));
      overCutout = !!hit.cutoutId;
      setCanvasCursor(scope, overCutout ? "move" : "crosshair");
    } else {
      setCanvasCursor(scope, "crosshair");
    }
    // Capture for `onModifiersChanged` — the staged polygon hovers are
    // modifier-sensitive (Ctrl/Cmd angle snap), like the drag previews.
    this.lastSample = s;
    this.lastP = p;
    // Regular-polygon stages (#931/#934): the hover rotates the aimed
    // polygon, rubber-bands the click-move-click side, or drives the auto-n
    // stage-2 preview.
    if (this.kind === "polygon" && this.polyGesture.hover(s, p)) return;
    if (this.kind === "polygon" && !this.startPt) {
      // Rubber-band hover, engine-owned flow (#804 IE3). The vertex/angle/grid
      // snap tiers + close-snap ring are injected via `resolveDraftHover` /
      // `onEmptyDraftHover`.
      this.drawEngine.onHover(s, p);
      // While hovering to close, the pending segment IS the closing edge back
      // to the start — suppress its ray (the engine emits it unconditionally).
      if (this.drawEngine.isHoveringToClose()) this.ctx.scene.clearGuideRays();
      return;
    }
    // Pre-press feedback (rect / ellipse / slot / freehand and the polygon's
    // first vertex): light the snap indicator so the *start* point's snap is
    // visible, not just the drag end. Skip while hovering an existing cutout —
    // that press starts a drag, not a new shape.
    if (overCutout) this.ctx.scene.setDrawSnapPoint(null);
    else this.snap(p);
  }

  override onUp(s: PointerSample, p: { x: number; y: number }) {
    if (this.kind === "offset") {
      this.offset.handleUp();
      return;
    }
    // Finish a drag-existing first if one is in progress.
    if (this.dragStart && this.dragCutoutId && this.dragPieceId) {
      const dx = p.x - this.dragStart.x;
      const dy = p.y - this.dragStart.y;
      const pieceId = this.dragPieceId;
      const cutoutId = this.dragCutoutId;
      this.resetDrag();
      if (Math.abs(dx) >= 0.001 || Math.abs(dy) >= 0.001) {
        const scope = this.ctx.scene.getPaperScope();
        this.snapTargets = null; // geometry changed — rebuild targets next use
        this.ctx.commit("Move cutout", (proj) =>
          translateCutoutInProject(proj, pieceId, cutoutId, dx, dy, scope),
        );
      }
      return;
    }
    if (this.kind === "polygon" && !this.startPt) {
      // Vertices are placed and the loop is closed in `onDown` (click near the
      // first vertex) — except for a pointer with no hover, whose press only
      // previews and whose RELEASE places the vertex (#1116).
      this.drawEngine.onUp(s, p);
      return;
    }

    let pathData: string | null = null;
    let freehandTrace: Vec2[] | null = null;
    let rounded = false;
    const scope = this.ctx.scene.getPaperScope();
    if (this.kind === "polygon" && this.startPt && this.currentPt) {
      // Regular-polygon release (#928/#931/#934): the shared gesture commits
      // the fixed-count ring or transitions into a stage (auto-n stage 2 /
      // click-move-click anchor / pinned-size aim anchor).
      const a = { x: this.startPt.x, y: this.startPt.y };
      const b = { x: this.currentPt.x, y: this.currentPt.y };
      const fromCenter = this.drawFromCenter;
      this.startPt = null;
      this.currentPt = null;
      this.buffer = [];
      this.drawFromCenter = false;
      setAngleHint(null);
      this.ctx.scene.setDrawSnapPoint(null);
      this.polyGesture.release(a, b, fromCenter);
      return;
    }
    if (this.kind === "rect" && this.startPt && this.currentPt) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 1 && box.height > 1) {
        const right = box.left + box.width;
        const bottom = box.top + box.height;
        pathData = `M${box.left} ${box.top} L${right} ${box.top} L${right} ${bottom} L${box.left} ${bottom} Z`;
        rounded = true;
      }
    } else if (this.kind === "ellipse" && this.startPt && this.currentPt) {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 1 && box.height > 1) {
        const tmp = new scope.Path.Ellipse(
          new scope.Rectangle(
            new scope.Point(box.left, box.top),
            new scope.Size(box.width, box.height),
          ),
        );
        pathData = tmp.pathData;
        tmp.remove();
      }
    } else if (this.kind === "freehand" && this.buffer.length > 2) {
      // Keep the trace so the cutout can be RE-smoothed later (#1016).
      freehandTrace = this.buffer.map((p) => ({ x: p.x, y: p.y }));
      pathData = smoothPathFromRawPoints(
        freehandTrace,
        this.freehandSmoothness,
        true,
        this.ctx.scene.getPaperScope(),
      );
    } else if (this.kind === "slot" && this.startPt && this.currentPt) {
      pathData = this.buildSlotPath(this.startPt, this.currentPt);
    }
    this.cleanupPreview();
    this.startPt = null;
    this.currentPt = null;
    this.buffer = [];
    this.lastSample = null;
    this.lastP = null;
    this.drawFromCenter = false;
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
    if (pathData)
      this.commitCutout(pathData, freehandTrace ?? undefined, rounded);
  }

  override onCancel() {
    this.offset.cancel();
    if (this.draggedItem) this.ctx.rerender();
    this.resetDrag();
    this.drawFromCenter = false;
    this.cleanupPreview();
    this.cancelDraft();
    this.buffer = [];
    this.startPt = null;
    this.currentPt = null;
    this.lastSample = null;
    this.lastP = null;
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
  }

  /** Modifier-key state changed (Shift) mid-drag — replay
   *  `onMove` with the new modifier values so the live preview
   *  re-evaluates size-snap (rect → square / ellipse → circle)
   *  and angle-snap (slot axis) without waiting for the next
   *  mouse-move event. No-op when there's no in-flight draft. */
  override onModifiersChanged(modifiers: {
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
  }) {
    if (!this.lastSample || !this.lastP) return;
    if (
      !this.startPt &&
      this.drawEngine.vertexCount === 0 &&
      !this.polyGesture.stageActive
    )
      return;
    const synth: PointerSample = { ...this.lastSample, ...modifiers };
    if (
      this.kind === "polygon" &&
      (this.drawEngine.vertexCount > 0 || this.polyGesture.stageActive)
    ) {
      this.onHover?.(synth, this.lastP);
    } else {
      this.onMove?.(synth, this.lastP);
    }
  }

  override onDeactivate() {
    // Drops any in-flight offset drag AND the hover arrow (#972).
    this.offset.cancel();
    this.cancelDraft();
    this.cleanupPreview();
    this.resetDrag();
    setCanvasCursor(this.ctx.scene.getPaperScope(), "");
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
  }

  private resetDrag() {
    this.dragPieceId = null;
    this.dragCutoutId = null;
    this.draggedItem = null;
    this.dragStart = null;
    this.dragLast = null;
  }

  /**
   * Commit a closed cutout path to the part it overlaps. Reject if the
   * cutout lies entirely outside every part (centroid not inside any part
   * and bbox doesn't overlap any part's bbox).
   */
  private commitCutout(
    pathData: string,
    freehandTrace?: Vec2[],
    rounded = false,
  ) {
    const scope = this.ctx.scene.getPaperScope();
    const tmp = new scope.CompoundPath({ pathData, insert: false });
    const cutBounds = tmp.bounds.clone();
    const cutCenter = tmp.bounds.center;
    tmp.remove();

    const project = this.ctx.history.state;
    // Resolve the target part (#783): never hidden, prefer the selected part,
    // else the top-most visible part under the cutout — not the bottom-most.
    // #779: a cutout must actually cut leather, so use the `"intersection"`
    // policy — the resolver's bbox-overlap fallback is fine for a hole but too
    // loose for a cutout drawn NEAR (not over) a part; the resolved part must
    // really overlap the cutout path or the draft is dropped.
    const target = resolveDrawTarget(
      project,
      { x: cutCenter.x, y: cutCenter.y },
      scope,
      this.ctx.scene.getSelection().pieceId,
      this.ctx.scene.getPartLayoutView(),
      cutBounds,
      { policy: "intersection", overlapPathData: pathData },
    );
    if (!target) return;
    const { pieceId, offset } = target;
    const targetPiece = project.pieces.find((p) => p.id === pieceId);
    const name = targetPiece ? nextCutoutName(targetPiece) : undefined;
    // Store in the part's ORIGINAL space so the spread render translate doesn't
    // double-offset the cutout (#786).
    const storedPathData =
      offset.dx !== 0 || offset.dy !== 0
        ? translatePathData(pathData, -offset.dx, -offset.dy, scope)
        : pathData;
    // The freehand trace has to make the same trip, or the re-derive would
    // rebuild the cutout at the DISPLAYED position and jump it (#1016).
    const storedTrace = (pts: Vec2[]): Vec2[] =>
      offset.dx !== 0 || offset.dy !== 0
        ? pts.map((p) => ({ x: p.x - offset.dx, y: p.y - offset.dy }))
        : pts;
    // Keep the cutout GEOMETRY exactly as drawn (the baked `pathData` is what
    // renders, so the shape never moves), but INHERIT the part's rotation frame
    // as metadata so the transform bbox is oriented the same as the part (#971).
    // Net effect the maker asked for: the object is conceptually rotated by the
    // part's angle (so its bbox tilts) while its shape is left where it was
    // drawn. The bbox becomes the part-oriented bounding box of the cutout; the
    // rotation pivot is still the cutout's own visual centre.
    const inheritRotation =
      targetPiece?.rotation !== undefined && targetPiece.rotationCenter
        ? {
            rotation: targetPiece.rotation,
            rotationCenter: targetPiece.rotationCenter,
          }
        : {};
    this.snapTargets = null; // geometry changed — rebuild targets next use
    // Seeded like a new construction line (#1099) — a curved cutout's anchors
    // start locked smooth, so the outline editor never has to guess the lock
    // back out of the handle geometry.
    const seedAnchors = computeDefaultSmoothAnchors(
      storedPathData,
      this.ctx.scene.getPaperScope(),
    );
    // A rectangle drawn with a corner radius (#1069) comes out rounded — the
    // same `cornerRounding` the Round tool writes, in the stored space.
    const cornerRounding =
      rounded && this.drawCornerRadius
        ? roundAllCorners(storedPathData, this.drawCornerRadius, scope)
        : undefined;
    this.ctx.commit("Add cutout", (proj) =>
      withCutout(proj, pieceId, {
        id: newId("cut"),
        pathData: storedPathData,
        name,
        ...(cornerRounding ? { cornerRounding } : {}),
        ...(seedAnchors ? { smoothAnchors: seedAnchors } : {}),
        // The trace is stored in the SAME space as `pathData` so the re-derive
        // lands where the cutout is (#1016).
        ...(freehandTrace
          ? {
              rawPoints: storedTrace(freehandTrace),
              smoothness: this.freehandSmoothness,
            }
          : {}),
        ...inheritRotation,
      }),
    );
  }

  /** Regular-polygon drag-out mode (#928) — see ShapeTool's twin. */
  private regularPolygonMode(s: PointerSample): boolean {
    if (this.kind !== "polygon") return false;
    return (
      this.ctx.settings.polygonEquilateral ||
      this.shouldSnapSizes(s) ||
      this.shouldSnapCenter(s)
    );
  }

  private updatePreview() {
    const scope = this.ctx.scene.getPaperScope();
    this.cleanupPreview();
    if (!this.startPt || !this.currentPt) return;
    if (this.kind === "polygon") {
      // Regular-polygon drag feedback (#928/#931/#934): the shared gesture
      // renders the ghost (via `renderPolygonGhost`), the first-side angle
      // ray, and the live panel mirrors.
      this.polyGesture.dragFeedback(
        this.lastSample,
        { x: this.startPt.x, y: this.startPt.y },
        { x: this.currentPt.x, y: this.currentPt.y },
        this.drawFromCenter,
      );
      return;
    } else if (this.kind === "rect") {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 0 && box.height > 0) {
        // Preview the draw-time corner radius the commit applies (#1069),
        // under the same rules as `roundAllCorners`: nothing below 0.5 mm,
        // and capped at half the shorter side (Paper clamps it there too).
        const rect = new scope.Rectangle(
          new scope.Point(box.left, box.top),
          new scope.Size(box.width, box.height),
        );
        const r =
          this.drawCornerRadius != null && this.drawCornerRadius >= 0.5
            ? this.drawCornerRadius
            : null;
        this.previewItem = r
          ? new scope.Path.Rectangle(rect, new scope.Size(r, r))
          : new scope.Path.Rectangle(rect);
        this.dimensionLabel.update(scope, this.ctx.viewport.view, box);
      }
    } else if (this.kind === "ellipse") {
      const box = this.resolveBoundingBox(this.startPt, this.currentPt);
      if (box.width > 0 && box.height > 0) {
        this.previewItem = new scope.Path.Ellipse(
          new scope.Rectangle(
            new scope.Point(box.left, box.top),
            new scope.Size(box.width, box.height),
          ),
        );
        this.dimensionLabel.update(scope, this.ctx.viewport.view, box);
      }
    } else if (this.kind === "freehand" && this.buffer.length > 1) {
      const path = new scope.Path({
        segments: this.buffer.map((b) => new scope.Point(b.x, b.y)),
      });
      this.previewItem = path;
    } else if (this.kind === "slot") {
      const data = this.buildSlotPath(this.startPt, this.currentPt);
      if (data) {
        this.previewItem = new scope.CompoundPath({ pathData: data });
      }
    }
    if (this.previewItem) {
      (this.previewItem as paper.Path).strokeColor = new scope.Color(
        PREVIEW_STROKE(),
      );
      (this.previewItem as paper.Path).strokeWidth = 0.75;
      (this.previewItem as paper.Path).dashArray = [4, 2];
      (this.previewItem as paper.Path).fillColor = null;
      this.previewItem.data = { ephemeral: true };
    }
  }

  // ---- DrawGestureHost implementation (#804 IE3) --------------------------
  // CutoutTool owns a `DrawGestureEngine` (`this.drawEngine`) that runs the
  // generic click-to-add polygon flow. Construction-only capabilities are off;
  // the leather vertex/grid snap + the cutout commit (target resolve + overlap
  // guard #779) are injected here.

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }
  getScene(): Scene {
    return this.ctx.scene;
  }
  previewColor(): string {
    return PREVIEW_STROKE();
  }
  isPolygon(): boolean {
    return this.kind === "polygon";
  }
  isPolyKind(): boolean {
    return this.kind === "polygon";
  }
  /** Leather has no line continuation (#328). */
  get continuationEnabled(): boolean {
    return false;
  }
  /** Leather has no per-vertex bezier handles (#217). */
  get bezierEnabled(): boolean {
    return false;
  }
  seedContinuation(): boolean {
    return false;
  }
  /** Leather previews don't light a mid-point self-snap halo. */
  selfSnapAt(): { pos: paper.Point; index: number } | null {
    return null;
  }

  /** DrawGestureHost: resolve a placed vertex through the leather snap tiers
   *  (vertex › angle › grid). Extracted from the old `onDown` polygon branch. */
  resolveDraftPlacement(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point {
    const scope = this.ctx.scene.getPaperScope();
    const snap = this.snap(p);
    if (snap.hit?.kind === "vertex") {
      setAngleHint(null);
      return new scope.Point(snap.point.x, snap.point.y);
    }
    if (this.shouldSnapAngle(s) && this.drawEngine.points.length > 0) {
      const last = this.drawEngine.points[this.drawEngine.points.length - 1]!;
      const sn = snapToCommonAngle(last, p);
      setAngleHint(sn.angleRad);
      return new scope.Point(sn.x, sn.y);
    }
    return new scope.Point(snap.point.x, snap.point.y);
  }

  /** DrawGestureHost: resolve the rubber-band hover through the same snap tiers,
   *  plus close-snap onto the first vertex. Extracted from the old `onHover`
   *  polygon branch. */
  resolveDraftHover(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point {
    const scope = this.ctx.scene.getPaperScope();
    const snap = this.snap(p);
    const pts = this.drawEngine.points;
    const last = pts[pts.length - 1]!;
    let pt: { x: number; y: number } = p;
    if (snap.hit?.kind === "vertex") {
      pt = snap.point;
      setAngleHint(null);
    } else if (this.shouldSnapAngle(s)) {
      const sn = snapToCommonAngle(last, p);
      pt = { x: sn.x, y: sn.y };
      setAngleHint(sn.angleRad);
    } else {
      if (snap.hit?.kind === "line" || snap.hit?.kind === "grid")
        pt = snap.point;
      setAngleHint(null);
    }
    // Close-snap: hovering near the first vertex (≥ 3 placed) snaps the pending
    // point home so the loop previews closed and the click commits (#779).
    const first = pts[0]!;
    const closing =
      pts.length >= 3 &&
      first.getDistance(new scope.Point(p.x, p.y)) <
        closeSnapRadius(scope.view?.zoom ?? 1);
    if (closing) {
      pt = { x: first.x, y: first.y };
      setAngleHint(null);
      this.ctx.scene.setDrawSnapPoint({ x: first.x, y: first.y });
    }
    return new scope.Point(pt.x, pt.y);
  }

  /** DrawGestureHost: `onHover` while the draft is still empty — light the
   *  first-click snap indicator. */
  onEmptyDraftHover(_s: PointerSample, p: { x: number; y: number }): void {
    this.snap(p);
  }

  /** DrawGestureHost: build the thin dashed red leather preview + close halo. */
  buildPreview(group: paper.Group, closing: boolean): void {
    const scope = this.ctx.scene.getPaperScope();
    const stroke = this.previewColor();
    const pts = this.drawEngine.points;
    const hover = this.drawEngine.hoverPt;
    // While closing, drop the trailing hover point (snapped onto the first
    // vertex) and render the loop closed so the user sees what will commit.
    const segs = closing ? [...pts] : hover ? [...pts, hover] : [...pts];
    const path = new scope.Path({ segments: segs });
    if (closing) path.closed = true;
    path.strokeColor = new scope.Color(stroke);
    path.strokeWidth = 0.75;
    path.dashArray = [4, 2];
    path.fillColor = null;
    group.addChild(path);
    if (closing) {
      const first = pts[0]!;
      const halo = new scope.Path.Circle(first, CLOSE_SNAP_RADIUS);
      halo.strokeColor = new scope.Color(stroke);
      halo.strokeColor.alpha = 0.6;
      halo.strokeWidth = 0.6;
      halo.strokeScaling = false;
      halo.fillColor = null;
      group.addChild(halo);
    }
  }

  /** DrawGestureHost: commit the current polygon draft as a cutout. Needs ≥ 3
   *  vertices; the overlap guard (#779) lives in `commitCutout`, which may
   *  still reject a draft that doesn't cut leather. */
  commitDraft(_closed: boolean): void {
    const pts = this.drawEngine.points;
    if (pts.length >= 3) {
      const buf = pts.map((p) => ({ x: p.x, y: p.y }));
      const data = pathFromBuffer(buf) + " Z";
      this.cancelDraft();
      this.commitCutout(data);
      return;
    }
    this.cancelDraft();
  }

  /** DrawGestureHost: full draft reset — clear the engine + this tool's own
   *  preview / snap + ray indicators. */
  cancelDraft(): void {
    this.polyGesture.cancel();
    this.drawEngine.reset();
    this.cleanupPreview();
    setAngleHint(null);
    this.ctx.scene.setDrawSnapPoint(null);
    this.ctx.scene.clearGuideRays();
    this.drawEngine.notifyDraftChange();
  }

  /** Observe polygon-draft vertex-count changes so the on-canvas finish/cancel
   *  fly-out (#779) can show/hide. Delegates to the engine. */
  setDraftChangeListener(fn: ((vertexCount: number) => void) | null) {
    this.drawEngine.setDraftChangeListener(fn);
  }

  /** Number of polygon vertices placed (0 = no draft). Regular-polygon
   *  stages (#931/#934) surface as 2 (✓ enabled) / 1 (✓ disabled). */
  get vertexCount(): number {
    if (this.kind !== "polygon") return 0;
    const staged = this.polyGesture.vertexCount();
    return staged > 0 ? staged : this.drawEngine.vertexCount;
  }

  /** Commit the in-progress polygon (Enter / ✓ / double-click). Delegates to
   *  the engine (commit ≥ 3 vertices, else discard). Returns true if there was
   *  a draft to act on. */
  handleFinish(): boolean {
    if (this.kind !== "polygon") return false;
    // Regular-polygon stages (#931/#934): Enter commits the staged preview.
    if (this.polyGesture.finish()) return true;
    return this.drawEngine.handleFinish();
  }

  /** Discard the in-progress polygon (Escape / ✗). Returns true if handled. */
  handleDiscard(): boolean {
    if (this.kind !== "polygon") return false;
    if (this.polyGesture.escape()) return true;
    if (this.drawEngine.vertexCount === 0) return false;
    this.cancelDraft();
    return true;
  }

  /** Double-click finishes the polygon, like the other draw tools. */
  handleDoubleClick(): boolean {
    return this.handleFinish();
  }

  private cleanupPreview() {
    if (this.previewItem) {
      this.previewItem.remove();
      this.previewItem = null;
    }
    this.dimensionLabel.remove();
  }

  // setCanvasCursor — invoked also from outside the class via the file-scope
  // helper at the bottom.

  /** Build a slot path between two points. If endpoints coincide (a click
   *  with no drag), default to a horizontal slot of length 4× width centered
   *  on the click. */
  /** Resolve the bounding box for a rect/ellipse drag — delegates to the
   *  shared {@link resolveDragBox}, threading the preset width / height and
   *  the latched "draw from centre" flag (Alt / centre snap toggle). */
  private resolveBoundingBox(
    start: paper.Point,
    current: paper.Point,
  ): { left: number; top: number; width: number; height: number } {
    return resolveDragBox(
      { x: start.x, y: start.y },
      { x: current.x, y: current.y },
      {
        presetWidth: this.presetWidth,
        presetHeight: this.presetHeight,
        fromCenter: this.drawFromCenter,
      },
    );
  }

  private buildSlotPath(start: paper.Point, end: paper.Point): string | null {
    const w = this.slotWidth;
    if (start.getDistance(end) < 0.5) {
      // Click-without-drag is already centred on the press point.
      const halfL = (w * SLOT_DEFAULT_LENGTH_RATIO) / 2;
      return makeSlotPathData(
        { x: start.x - halfL, y: start.y },
        { x: start.x + halfL, y: start.y },
        w,
      );
    }
    // From-centre (Alt / centre snap) → press point is the midpoint (#389).
    const { a, b } = resolveDragAxis(start, end, this.drawFromCenter);
    return makeSlotPathData(a, b, w);
  }

  /** Offset kind (#972): a typed distance fixes the inset / outset magnitude so
   *  a click on an edge commits at exactly that value; null → drag-defined. */
  setPresetOffsetDistance(mm: number | null): void {
    this.offset.setPresetDistance(mm);
  }

  /** Rounded-corner preference for the next outline offset (#423) — the
   *  tool side of the same choice the construction Offset sub-tool has. */
  setOffsetRoundedCorners(on: boolean): void {
    this.offset.setRoundedCorners(on);
  }

  /** Offset kind (#972): mirror the live drag distance back into the panel. */
  setLiveOffsetDistanceCallback(fn: ((mm: number) => void) | undefined): void {
    this.offset.setLiveDistanceCallback(fn);
  }

  // -------------------------------------------------------------------
  // OutlineOffsetHost (#972) — the Offset shape kind
  // -------------------------------------------------------------------
  // `getScope` / `getScene` come from the `DrawGestureHost` block above; the
  // two host interfaces overlap, so one implementation serves both.

  /** Cutout mode offsets the same shared source set (`offsetSourceOutlines`),
   *  but the legal directions differ BY SOURCE, because only one of them has a
   *  containment rule. From a PART outline it can only inset: a cutout has to
   *  overlap its piece, and an outset would put the result off the leather
   *  entirely, so an outward drag clamps to the inset rather than dead-ending.
   *  From another CUTOUT both ways are legal — a larger cutout ringing an
   *  existing one is still inside the part. */
  offsetCandidates(): OffsetCandidate[] {
    return offsetSourceOutlines(this.ctx.history.state).map((src) => ({
      ...src,
      allowInside: true,
      allowOutside: src.cutoutId !== undefined,
    }));
  }

  commitOffset(source: OffsetCandidate, pathData: string): void {
    const owner = this.ctx.history.state.pieces.find(
      (pc) => pc.id === source.pieceId,
    );
    const cutoutId = newId("cut");
    this.snapTargets = null; // geometry changed — rebuild targets next use
    this.ctx.commit("Offset cutout", (proj) =>
      withCutout(proj, source.pieceId, {
        id: cutoutId,
        pathData,
        ...(owner ? { name: nextCutoutName(owner) } : {}),
      }),
    );
    this.ctx.setSelection({
      pieceId: source.pieceId,
      decorationId: null,
      cutoutId,
    });
  }

  onOffsetTooDeep(): void {
    showCanvasToast("Offset too deep — the outline collapses");
  }
}

function setCanvasCursor(scope: paper.PaperScope, cursor: string) {
  const el = scope.view?.element as HTMLCanvasElement | undefined;
  if (el) el.style.cursor = cursor;
}
